import * as Sentry from '@sentry/vue';
import store from '@/store';
import { POSTHOG_FLAGS } from '@/constants/posthogFlags';
import { isPostHogFlagEnabled } from '@/utils/posthogFlags';

const DEBUG_STORAGE_KEY = 'telehealth_audio_debug';

function configuredUserIds() {
  return new Set(
    (import.meta.env.VITE_TELEHEALTH_AUDIO_DEBUG_USER_IDS || '')
      .split(',')
      .map(id => id.trim())
      .filter(Boolean)
  );
}

function isLocallyEnabled() {
  try {
    return localStorage.getItem(DEBUG_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function isTelehealthAudioDebugEnabled() {
  const userId = String(store.state.auth.current_user?.id || '');
  return (
    isLocallyEnabled() ||
    configuredUserIds().has(userId) ||
    isPostHogFlagEnabled(POSTHOG_FLAGS.TELEHEALTH_DIAGNOSTICS)
  );
}

function shouldLogToConsole() {
  return import.meta.env.DEV || isTelehealthAudioDebugEnabled();
}

export function describeAudioTrack(track) {
  if (!track) return { present: false };
  const settings = track.getSettings?.() || {};
  return {
    present: true,
    kind: track.kind,
    readyState: track.readyState,
    enabled: track.enabled,
    muted: track.muted,
    contentHint: track.contentHint || undefined,
    channelCount: settings.channelCount,
    sampleRate: settings.sampleRate,
    echoCancellation: settings.echoCancellation,
    noiseSuppression: settings.noiseSuppression,
    autoGainControl: settings.autoGainControl,
  };
}

/**
 * Records metadata-only call diagnostics. Audio data, transcript text, device
 * labels, device IDs, patient details, and work IDs must not be passed here.
 *
 * Sentry reporting is opt-in per user through the `telehealth-diagnostics`
 * PostHog flag. The environment allowlist and local `telehealth_audio_debug=1`
 * override remain available for emergency support use.
 */
export function logTelehealthAudioDiagnostic(
  message,
  data = {},
  { level = 'info', capture = false } = {}
) {
  if (shouldLogToConsole()) {
    const method =
      level === 'error' ? 'error' : level === 'warning' ? 'warn' : 'info';
    console[method](`[TelehealthAudio] ${message}`, data);
  }

  if (!isTelehealthAudioDebugEnabled()) return;

  Sentry.addBreadcrumb({
    category: 'telehealth.audio',
    level,
    message,
    data,
  });

  if (capture) {
    Sentry.captureMessage(`Telehealth audio: ${message}`, {
      level,
      tags: { diagnostic: 'telehealth_audio' },
      extra: data,
    });
  }
}
